import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getTrackUrlForLocale } from '../../utils/localeUtils';
import {
  getNextTrackIndex,
  getPreviousTrackIndex,
} from '../../utils/shuffleUtils';
import { useMusicState } from '../storeHooks/useMusicState';
import { useAudioLoader } from './useAudioLoader';
import { useAudioTimeTracking } from './useAudioTimeTracking';
import { useMediaSession } from './useMediaSession';
import { usePlaybackStateSync } from './usePlaybackStateSync';
import { useShuffleOrder } from './useShuffleOrder';
import { useUserInteraction } from './useUserInteraction';

interface UseMusicPlayerProps {
  isMinimized?: boolean;
}

export const useMusicPlayer = ({
  isMinimized = false,
}: UseMusicPlayerProps = {}) => {
  const {
    activePlaylist,
    setActivePlaylist,
    shuffleEnabled,
    shuffleOrder,
    repeatMode,
    volume,
    setVolume,
    preferredLocale,
    setRepeatMode,
    musicPlaying: musicPlayingFromStore,
    setMusicPlaying,
  } = useMusicState();

  const location = useLocation();

  // Audio and video refs
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Player state (synced to Redux)
  const [isPlaying, setIsPlayingState] = useState(false);
  const setIsPlaying = useCallback(
    (value: boolean) => {
      setIsPlayingState(value);
      setMusicPlaying(value);
    },
    [setMusicPlaying],
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  // Mobile audio context management
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [pendingPlay, setPendingPlay] = useState(false);

  // Track the currently loaded track to prevent unnecessary reloads
  const currentTrackRef = useRef<{
    albumId: string;
    trackIndex: number;
    trackId: string;
  } | null>(null);
  const isSeekingRef = useRef(false);
  const bufferingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refs to track current state values for event handlers
  const userHasInteractedRef = useRef(userHasInteracted);
  const pendingPlayRef = useRef(pendingPlay);

  // Update refs when state changes
  userHasInteractedRef.current = userHasInteracted;
  pendingPlayRef.current = pendingPlay;

  // Use sub-hooks for specific functionality
  useShuffleOrder();

  useUserInteraction({
    userHasInteracted,
    pendingPlay,
    setUserHasInteracted,
    setPendingPlay,
  });

  useAudioTimeTracking({
    audioRef,
    isPlaying,
    setProgress,
    setCurrentTime,
    setDuration,
  });

  useAudioLoader({
    audioRef,
    currentTrackRef,
    isSeekingRef,
    userHasInteractedRef,
    pendingPlayRef,
    isMinimized,
    videoRef,
    setIsLoading,
    setIsPlaying,
    setProgress,
    setCurrentTime,
    setDuration,
    setPendingPlay,
  });

  // play back sync
  usePlaybackStateSync({
    audioRef: audioRef.current,
    videoRef: videoRef.current,
    setIsPlaying,
    isSeekingRef,
  });

  useEffect(() => {
    if (
      !musicPlayingFromStore &&
      audioRef.current &&
      !audioRef.current.paused
    ) {
      try {
        audioRef.current.pause();
      } catch {
        // ignore
      }
      if (videoRef.current && !videoRef.current.paused) {
        try {
          videoRef.current.pause();
        } catch {
          // ignore
        }
      }
      setIsPlayingState(false);
    }
  }, [musicPlayingFromStore]);

  useEffect(() => {
    const audio = audioRef.current;
    if (
      musicPlayingFromStore &&
      activePlaylist &&
      audio &&
      audio.paused &&
      audio.src &&
      audio.readyState >= 2
    ) {
      audio.play().then(
        () => {
          setIsPlayingState(true);
          if (videoRef.current && videoRef.current.paused) {
            videoRef.current.play().catch(() => {});
          }
        },
        () => {
          setIsPlayingState(false);
          setMusicPlaying(false);
        },
      );
    }
  }, [
    musicPlayingFromStore,
    activePlaylist?.album?.id,
    activePlaylist?.currentIndex,
    setMusicPlaying,
  ]);

  // Sync volume changes with audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      // On iOS, volume is read-only — use muted property as fallback
      audioRef.current.muted = volume === 0;
    }
  }, [volume]);

  // Track buffering state (for showing spinner during seek buffering)
  // Clear buffering when 'playing' event fires, and cancel any pending buffering timeout
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlaying = () => {
      // Cancel pending buffering timeout if audio starts playing quickly
      if (bufferingTimeoutRef.current) {
        clearTimeout(bufferingTimeoutRef.current);
        bufferingTimeoutRef.current = null;
      }
      setIsBuffering(false);
    };

    audio.addEventListener('playing', handlePlaying);

    return () => {
      audio.removeEventListener('playing', handlePlaying);
    };
  }, [isPlaying]);

  // Format time in MM:SS format
  const formatTime = useCallback((seconds: number) => {
    if (isNaN(seconds) || seconds < 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  }, []);

  // Mobile audio initialization
  const initializeMobileAudio = useCallback(async () => {
    if (userHasInteracted || !audioRef.current) return;

    try {
      // Create a silent audio context to unlock mobile audio
      const audioContext = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Try to load and prepare the audio element
      const audio = audioRef.current;
      audio.muted = false;
      audio.volume = volume;

      // Load the current track if available
      if (
        activePlaylist &&
        activePlaylist.album.tracks[activePlaylist.currentIndex]
      ) {
        const currentTrack =
          activePlaylist.album.tracks[activePlaylist.currentIndex];
        let trackUrl = getTrackUrlForLocale(currentTrack, preferredLocale);

        // Skip processing for absolute URLs (http/https)
        if (
          !trackUrl.startsWith('http://') &&
          !trackUrl.startsWith('https://')
        ) {
          // Handle relative URLs
          if (trackUrl.startsWith('/')) {
            trackUrl = trackUrl.slice(1);
          }

          if (
            !trackUrl.toLowerCase().endsWith('.mp3') &&
            !trackUrl.toLowerCase().endsWith('.wav') &&
            !trackUrl.includes('api.musicgpt.com/api/public/v1/MusicAI/stream')
          ) {
            trackUrl += '.mp3';
          }
        }

        audio.src = trackUrl;
        audio.load();
      }

      setUserHasInteracted(true);

      // If there was a pending play request, execute it now
      if (pendingPlay) {
        setPendingPlay(false);
        handlePlayPause();
      }
    } catch (error) {
      console.error('Failed to initialize mobile audio:', error);
    }
  }, [activePlaylist, pendingPlay]);

  // Play / Pause
  const handlePlayPause = useCallback(async () => {
    if (!audioRef.current) return;

    // On mobile direct URL access, initialize audio context on first user interaction
    if (!userHasInteracted && !pendingPlay) {
      await initializeMobileAudio();
      return; // initializeMobileAudio will call handlePlayPause again if needed
    }

    if (isPlaying) {
      const audio = audioRef.current;

      let pauseSucceeded = false;
      try {
        audio.pause();
        pauseSucceeded = true;
      } catch (error) {
        console.error('Error pausing audio:', error);
      }

      if (videoRef.current) {
        try {
          videoRef.current.pause();
        } catch (error) {
          console.error('Error pausing video:', error);
        }
      }

      if (pauseSucceeded) {
        setIsPlaying(false);
      }
    } else {
      try {
        // Clear any pending play state
        setPendingPlay(false);

        await audioRef.current.play();
        if (videoRef.current) {
          videoRef.current.play().catch(() => {
            console.warn('Video play failed, continuing with audio only');
          });
        }
        setIsPlaying(true);
      } catch (error) {
        console.error('Audio play failed:', error);
        setIsPlaying(false);

        // On mobile direct URL access, this might be due to autoplay restrictions
        if (!userHasInteracted) {
          setPendingPlay(true);
        }
      }
    }
  }, [isPlaying, userHasInteracted, pendingPlay, initializeMobileAudio]);

  // Auto-start playback when user directly accesses player routes
  useEffect(() => {
    const isDirectPlayerAccess = location.pathname.match(
      /^\/player\/[^/]+\/?([^/]+)?$/,
    );

    if (
      isDirectPlayerAccess &&
      activePlaylist &&
      audioRef.current &&
      !userHasInteracted &&
      !pendingPlay
    ) {
      // Set user has interacted to bypass autoplay restrictions for direct player access
      setUserHasInteracted(true);
      // Trigger playback after a short delay to ensure audio is loaded
      const timer = setTimeout(() => {
        handlePlayPause();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    location.pathname,
    activePlaylist,
    userHasInteracted,
    pendingPlay,
    handlePlayPause,
    setUserHasInteracted,
  ]);

  // Next track
  const handleNext = useCallback(() => {
    if (!activePlaylist) return false;

    // If repeat-one is enabled, disable it when user clicks next
    if (repeatMode === 'one') {
      setRepeatMode('off');
    }

    const nextIndex = getNextTrackIndex(
      activePlaylist.currentIndex,
      shuffleEnabled,
      shuffleOrder,
      activePlaylist.album.tracks.length,
      repeatMode === 'one' ? 'off' : repeatMode,
      activePlaylist.album.tracks, // Pass tracks to skip unavailable ones
    );

    if (nextIndex !== null) {
      setActivePlaylist({
        ...activePlaylist,
        currentIndex: nextIndex,
      });
      return true;
    }

    return false;
  }, [
    activePlaylist,
    shuffleEnabled,
    shuffleOrder,
    repeatMode,
    setActivePlaylist,
    setRepeatMode,
  ]);

  // Previous track
  const handlePrev = useCallback(() => {
    if (!activePlaylist) return;

    // If repeat-one is enabled, disable it when user clicks prev
    if (repeatMode === 'one') {
      setRepeatMode('off');
    }

    const prevIndex = getPreviousTrackIndex(
      activePlaylist.currentIndex,
      shuffleEnabled,
      shuffleOrder,
      activePlaylist.album.tracks.length,
      repeatMode === 'one' ? 'off' : repeatMode,
      activePlaylist.album.tracks, // Pass tracks to skip unavailable ones
    );

    if (prevIndex !== null) {
      setActivePlaylist({
        ...activePlaylist,
        currentIndex: prevIndex,
      });
    } else {
      // Already at first track → restart current track
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        if (isPlaying) {
          audioRef.current.play().catch((error) => {
            console.error('Error playing audio after locale change:', error);
            setIsPlaying(false);
          });
        }
      }
    }
  }, [
    activePlaylist,
    shuffleEnabled,
    shuffleOrder,
    repeatMode,
    isPlaying,
    setActivePlaylist,
    setRepeatMode,
  ]);

  // Seek functionality
  const handleSeek = useCallback((percentage: number) => {
    if (!audioRef.current) return;
    const audio = audioRef.current;

    // Validate input percentage
    if (!isFinite(percentage)) {
      console.warn('Cannot seek: Invalid percentage provided:', percentage);
      return;
    }

    // Check if audio is seekable - use isFinite instead of isNaN
    if (!isFinite(audio.duration) || audio.duration <= 0) {
      console.warn('Cannot seek: Audio duration not available or invalid:', {
        duration: audio.duration,
        readyState: audio.readyState,
        networkState: audio.networkState,
      });
      return;
    }

    // Check if audio is ready for seeking
    if (audio.readyState < 2) {
      // HAVE_CURRENT_DATA
      console.warn('Cannot seek: Audio not ready for seeking');
      return;
    }

    // Ensure percentage is within valid range
    const clampedPercentage = Math.max(0, Math.min(100, percentage));
    const newTime = (clampedPercentage / 100) * audio.duration;

    // Check if the new time is valid and finite
    if (!isFinite(newTime) || newTime < 0 || newTime > audio.duration) {
      console.warn('Cannot seek: Invalid time calculated:', {
        newTime,
        percentage: clampedPercentage,
        duration: audio.duration,
        isFinite: isFinite(newTime),
      });
      return;
    }

    try {
      // Set seeking flag to prevent audio reloading
      isSeekingRef.current = true;

      // Store current playing state
      const wasPlaying = !audio.paused;

      // Clear any existing buffering timeout
      if (bufferingTimeoutRef.current) {
        clearTimeout(bufferingTimeoutRef.current);
        bufferingTimeoutRef.current = null;
      }

      // Show buffering indicator after a delay (only if buffering takes > 200ms)
      // This prevents flickering for quick seeks
      if (wasPlaying) {
        bufferingTimeoutRef.current = setTimeout(() => {
          setIsBuffering(true);
        }, 200);
      }

      // Temporarily pause to prevent issues during seek
      if (wasPlaying) {
        audio.pause();
      }

      // Perform the seek operation with additional validation
      audio.currentTime = newTime;

      // Wait a brief moment for the seek to complete
      setTimeout(() => {
        // Validate the seek was successful
        if (!isFinite(audio.currentTime)) {
          console.error(
            'Seek resulted in non-finite currentTime:',
            audio.currentTime,
          );
          // Try to reset to a safe value
          try {
            audio.currentTime = 0;
          } catch (resetError) {
            console.error(
              'Failed to reset currentTime after seek error:',
              resetError,
            );
          }
          isSeekingRef.current = false;
          return;
        }

        // Sync video time if video exists (with validation)
        if (videoRef.current && isFinite(newTime)) {
          try {
            videoRef.current.currentTime = newTime;
          } catch (videoError) {
            console.warn('Failed to sync video time:', videoError);
          }
        }

        // Resume playback if it was playing before
        if (wasPlaying) {
          audio.play().catch((error) => {
            console.error('Failed to resume playback after seek:', error);
            setIsPlaying(false);
          });
        }

        // Update progress state to reflect the actual seek position (with validation)
        if (
          isFinite(audio.currentTime) &&
          isFinite(audio.duration) &&
          audio.duration > 0
        ) {
          const actualProgress = (audio.currentTime / audio.duration) * 100;
          if (isFinite(actualProgress)) {
            setProgress(Math.max(0, Math.min(100, actualProgress)));
            setCurrentTime(audio.currentTime);
          }
        }

        // Clear seeking flag
        isSeekingRef.current = false;
      }, 50); // Small delay to allow seek to complete
    } catch (error) {
      console.error('Error during seek operation:', error);
      // Clear seeking flag even on error
      isSeekingRef.current = false;
    }
  }, []);

  // Volume control
  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      const clampedVolume = Math.max(0, Math.min(1, newVolume));
      setVolume(clampedVolume);

      // Update audio element volume immediately
      if (audioRef.current) {
        audioRef.current.volume = clampedVolume;
        // On iOS, volume is read-only — use muted property as fallback
        audioRef.current.muted = clampedVolume === 0;
      }
    },
    [setVolume],
  );

  // Track selection
  const handleTrackSelect = useCallback(
    (index: number) => {
      if (!activePlaylist) return;

      setActivePlaylist({
        ...activePlaylist,
        currentIndex: index,
      });
    },
    [activePlaylist, setActivePlaylist],
  );

  // Auto next on end
  const handleEnded = useCallback(() => {
    if (repeatMode === 'one') {
      // Repeat current track - restart from beginning
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
            if (videoRef.current) {
              videoRef.current.currentTime = 0;
              videoRef.current.play().catch((error) => {
                console.error('Error playing video on repeat:', error);
              });
            }
          })
          .catch((error) => {
            console.error('Failed to repeat track:', error);
            setIsPlaying(false);
          });
      }
    } else {
      const advanced = handleNext();
      if (!advanced) {
        setIsPlaying(false);
      }
    }
  }, [repeatMode, handleNext, setIsPlaying]);

  // Media Session API for lock screen controls
  useMediaSession({
    audioRef,
    isPlaying,
    handlePlayPause,
    handleNext,
    handlePrev,
    handleSeek,
  });

  return {
    // Refs
    audioRef,
    videoRef,

    // State
    isPlaying,
    currentTime,
    duration,
    progress,
    isLoading,
    isBuffering,
    userHasInteracted,
    pendingPlay,
    volume,

    // Handlers
    handlePlayPause,
    handleNext,
    handlePrev,
    handleSeek,
    handleTrackSelect,
    handleEnded,
    handleVolumeChange,
    initializeMobileAudio,

    // Utilities
    formatTime,

    // Internal state setters (for component use)
    setIsPlaying,
    setCurrentTime,
    setDuration,
    setProgress,
    setIsLoading,
    setUserHasInteracted,
    setPendingPlay,

    // Internal refs (for component use)
    currentTrackRef,
    isSeekingRef,
    userHasInteractedRef,
    pendingPlayRef,
  };
};
